import { useMutation, UseMutationOptions } from '@tanstack/react-query';

import FileProcessingApiHandler from 'app/services/fileProcessingApiHandler';
import {
  UploadResponsePayload,
  ApprovedEarningsTimesheetResponse,
} from 'app/types/fileProcessingService';

export interface UploadAETFileRequest {
  companyId: string;
  payCalendarId: string;
  file: File;
  metadata?: {
    payCalendarId: string;
    startDate: string;
    endDate: string;
  };
}

export interface UploadAETFileResponse {
  uploadResponse: UploadResponsePayload;
  fileUpload: ApprovedEarningsTimesheetResponse;
}

// Real FPS service implementation
const aetFileService = {
  uploadAETFile: async ({
    companyId,
    file,
  }: UploadAETFileRequest): Promise<UploadAETFileResponse> => {
    // Step 1: Get pre-signed URL for upload
    const uploadResponse = await FileProcessingApiHandler.getUploadPreSignedUrl(
      file.name,
      companyId,
      'APPROVED_EARNINGS_TIMESHEET'
    );

    // Step 2: Upload file to S3 using pre-signed URL
    const uploadToS3Response = await fetch(uploadResponse.url, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': file.type,
      },
    });

    if (!uploadToS3Response.ok) {
      throw new Error(`Failed to upload file to S3: ${uploadToS3Response.statusText}`);
    }

    // Step 3: Request file analysis
    await FileProcessingApiHandler.requestFileAnalysis(uploadResponse.id);

    // Step 4: Get the file details
    const fileResponse = await FileProcessingApiHandler.getFileById(uploadResponse.id);

    const result = {
      uploadResponse,
      fileUpload: fileResponse,
    };

    return result;
  },
};

const useUploadAETFile = ({
  onSuccess,
  onError,
}: UseMutationOptions<UploadAETFileResponse, Error, UploadAETFileRequest>) => {
  return useMutation<UploadAETFileResponse, Error, UploadAETFileRequest>(
    (request: UploadAETFileRequest) => aetFileService.uploadAETFile(request),
    { onSuccess, onError }
  );
};

export default useUploadAETFile;
